import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    Menu,
    MenuItem,
    IconButton,
    Chip
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import DistributionCenterInventoryService from '@api/DistributionCenterInventories/DistributionCenterInventoryCtrl';
import { DistributionCenterInventory } from '@api/DistributionCenterInventories/DistributionCenterInventory';
import { getStockStatus, getStockStatusColor } from '@api/Inputs/Input';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { hasPermission } from '@utils/Permissions';
import { ProfileTypes } from '@api/Users/User';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { setActivityDrawer } from '@store/appStore';

interface ListInventoryProps {
    distributionCenterId: string;
    onAddInputs: () => void;
    onRemoveInputs: () => void;
}

const ListInventory: React.FunctionComponent<ListInventoryProps> = (props) => {
    const { distributionCenterId, onAddInputs, onRemoveInputs } = props;
    const dispatch = useDispatch();
    const distributionCenterInventoryService = useMemo(() => new DistributionCenterInventoryService(), []);
    const [inventory, setInventory] = useState<DistributionCenterInventory[]>([]);
    const [currentItemInventory, setCurrentInputInventory] = useState<DistributionCenterInventory | undefined>(
        undefined
    );

    const [filters, setFilters] = useState({ search: '' });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: DistributionCenterInventory) => {
        setAnchorEl(event.currentTarget);
        setCurrentInputInventory(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewHistory = () => {
        handleMenuClose();
        if (currentItemInventory?.input?.id) {
            dispatch(
                setActivityDrawer({
                    isOpen: true,
                    section: 'input',
                    sectionId: currentItemInventory.input.id
                })
            );
        }
    };

    const getData = async () => {
        showLoader();
        try {
            const dataInventory = await distributionCenterInventoryService.getInventory(distributionCenterId, true);
            setInventory(dataInventory);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, [distributionCenterId]);

    const columns = useMemo<ResponsiveGridColDef<DistributionCenterInventory>[]>(
        () => [
            {
                field: 'category',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Categoria
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography>{row.input.inputCategoryData?.name || 'Sin categoria'}</Typography>
                ),
                valueGetter: ({ row }) => row.input.inputCategoryData?.name
            },
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.input.name}</Typography>,
                valueGetter: ({ row }) => row.input.name
            },
            {
                field: 'presentation',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Presentación
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography>{`${row.input.presentation.value} ${row.input.presentation.unit}`}</Typography>
                )
            },
            {
                field: 'unit',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Unidad Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`${row.input.unit.value} ${row.input.unit.unit}`}</Typography>
            },
            {
                field: 'purchaseCost',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Compra
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.purchaseCost}`}</Typography>
            },
            {
                field: 'costOfSale',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Venta
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.costOfSale}`}</Typography>
            },
            {
                field: 'stock',
                flex: 1,
                showInMobile: true,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Stock
                    </Typography>
                ),
                renderCell: ({ row }) => {
                    return <Typography>{row.stock}</Typography>;
                }
            },
            {
                field: 'status',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Stack>
                        <Chip
                            label={getStockStatus(row.stock, row.input)}
                            color={getStockStatusColor(row.stock, row.input) as any}
                            size="small"
                        />
                    </Stack>
                )
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={5} />
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={2}>
                            {hasPermission([ProfileTypes.Admin]) && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={onRemoveInputs}
                                >
                                    Registrar salidas
                                </Button>
                            )}
                            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onAddInputs}>
                                Registrar entradas
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar insumo"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={inventory}
                columns={columns}
                getRowId={(row) => `${row.id}-${row.input.id}`}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {/* "Editar" se quitó (2026-08-19, pedido del negocio): guardaba una
                    copia editada del insumo DENTRO del inventario del CEDIS, creando
                    una bifurcación silenciosa contra el catálogo global (mismo mal de
                    los snapshots que nunca se refrescan), y permitía fijar el stock a
                    mano. El insumo se edita en Catálogo; el stock del CEDIS se mueve
                    por Registrar entradas / Registrar salidas y por los pedidos que
                    surte a las sucursales. */}
                <MenuItem onClick={handleViewHistory}>Ver historial</MenuItem>
            </Menu>
        </Stack>
    );
};

export default ListInventory;
